import { FC } from "react";

import { Box, chakra } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import Checkbox from "@components/Checkbox";
import { FullWidthVStack } from "@components/common";
import EmailInput from "@components/EmailInput";
import PhoneInput from "@components/PhoneInput";
import TextInput from "@components/TextInput";
import { useRouter } from "@lib/router";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { DOMAIN } from "@utils/constants";

import { DeliveryMethod, ORDER_FIELDS_MAX_LENGTHS, OrderFields } from "../../constants";
import useOrderFormik from "../../hooks/useOrderFormik";
import getIsSenderNameFieldVisible from "../../utils/getIsSenderNameFieldVisible";

const ContactPerson: FC = () => {
  const { t } = useTranslation();

  const { values } = useOrderFormik();

  const startingCountry = values[OrderFields.STARTING_COUNTRY];
  const destinationCountry = values[OrderFields.DESTINATION_COUNTRY];
  const deliveryMethod = values[OrderFields.DELIVERY_METHOD] as DeliveryMethod;
  const isDomestic = values[OrderFields.IS_DOMESTIC];

  const isSenderAsContactPerson = values[OrderFields.SENDER_AS_CONTACT_PERSON];

  const { defaultLocale } = useRouter();

  const isSenderNameVisible = getIsSenderNameFieldVisible(
    deliveryMethod!,
    startingCountry!,
    destinationCountry!,
    defaultLocale!
  );

  const isSenderNameRequired = deliveryMethod === DeliveryMethod.DoorToDoor || !isDomestic;

  return (
    <FullWidthVStack spacing="0.5rem" width={responsiveStyle({ mobile: "100%", desktop: "50%" })}>
      <Box>
        <Checkbox isChecked={isSenderAsContactPerson} name={OrderFields.SENDER_AS_CONTACT_PERSON}>
          <chakra.span fontSize={responsiveStyle({ mobile: "0.875rem", desktop: "1rem" })}>
            {t("order-sender-use_sender_as_contact_person")}
          </chakra.span>
        </Checkbox>
      </Box>
      {!isSenderAsContactPerson && (
        <>
          {isSenderNameVisible && (
            <Box>
              <TextInput
                name={OrderFields.CONTACT_PERSON_NAME}
                label={`${t("order-sender-contact_person-name")} ${!isSenderNameRequired ? t("common-optional") : ""}`}
                id="order-contact-name"
                maxLength={ORDER_FIELDS_MAX_LENGTHS.name}
              />
            </Box>
          )}
          <Box>
            <PhoneInput
              name={OrderFields.CONTACT_PERSON_PHONE_NUMBER}
              label={t("order-sender-contact_person-mobile_number")}
              id="order-contact-phone"
              pinnedPhoneCodes={DOMAIN.DOMAIN_PHONE_NUMBERS[defaultLocale ?? ""]}
              defaultCountry={startingCountry!}
            />
          </Box>
          <Box>
            <EmailInput
              name={OrderFields.CONTACT_PERSON_EMAIL}
              label={t("order-sender-contact_person-email")}
              id="order-contact-email"
              maxLength={ORDER_FIELDS_MAX_LENGTHS.email}
            />
          </Box>
        </>
      )}
    </FullWidthVStack>
  );
};
export default ContactPerson;
